import React, { useState } from 'react';
import axios from 'axios';
import { Monitor, User, Mail, Lock, Phone, Briefcase, Globe, CheckCircle2, AlertTriangle, ChevronRight, ChevronLeft, Shield } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STEPS = ['Basic Info', 'Professional', 'KYC & Bank', 'NDA', 'Review'];
const ROLE_OPTIONS = [
  { value: 'modeler_3d', label: '3D Modeler', pipeline: 'modeling' },
  { value: 'texture_artist', label: 'Texture Artist', pipeline: 'texturing' },
  { value: 'rigging_artist', label: 'Rigging Artist', pipeline: 'rigging' },
  { value: 'animator', label: 'Animator', pipeline: 'animation' },
  { value: 'lighting_artist', label: 'Lighting Artist', pipeline: 'lighting' },
  { value: 'compositor', label: 'Compositor', pipeline: 'compositing' },
  { value: 'fx_artist', label: 'FX / VFX Artist', pipeline: 'compositing' },
  { value: 'concept_artist', label: 'Concept Artist', pipeline: 'modeling' },
  { value: 'character_designer', label: 'Character Designer', pipeline: 'modeling' },
  { value: 'storyboard_artist', label: 'Storyboard Artist', pipeline: '' },
  { value: 'video_editor', label: 'Video Editor', pipeline: 'compositing' },
  { value: 'sound_designer', label: 'Sound Designer', pipeline: '' },
  { value: 'music_composer', label: 'Music Composer', pipeline: '' },
  { value: 'colorist', label: 'Colorist', pipeline: 'compositing' },
  { value: 'screenwriter', label: 'Screenwriter', pipeline: '' },
];
const TOOLS = ['Blender', 'Maya', '3ds Max', 'ZBrush', 'Substance Painter', 'After Effects', 'Nuke', 'Houdini', 'Unreal Engine', 'Premiere Pro', 'DaVinci Resolve', 'Photoshop', 'Illustrator'];

export default function FreelancerRegisterPage() {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [f, setF] = useState({
    full_name: '', email: '', password: '', confirm_password: '', phone: '', specialist_roles: [],
    skills: '', tools: [], portfolio_url: '', showreel_url: '', years_experience: '', city: '',
    pan_number: '', aadhar_number: '', bank_account_name: '', bank_account_number: '', bank_ifsc: '', bank_name: '',
    nda_signed: false, nda_signature: '',
  });

  const u = (k, v) => setF(p => ({ ...p, [k]: v }));
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex = /^[\d+\-\s]{10,15}$/;
  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

  const validate = () => {
    if (step === 0) {
      if (!f.full_name || f.full_name.length < 2) return 'Full name is required';
      if (!f.email) return 'Email is required';
      if (!emailRegex.test(f.email)) return 'Enter a valid email (e.g., name@email.com)';
      if (!f.password || f.password.length < 6) return 'Password must be at least 6 characters';
      if (f.password !== f.confirm_password) return 'Passwords do not match';
      if (!f.phone) return 'Phone number is required';
      if (!phoneRegex.test(f.phone.replace(/\s/g, ''))) return 'Enter a valid phone number (digits only, 10+)';
      if (f.specialist_roles.length === 0) return 'Select at least 1 role (your expertise). This determines what work you receive.';
    }
    if (step === 1) {
      if (!f.skills && f.tools.length === 0) return 'Add at least one skill or select a tool';
    }
    if (step === 2) {
      if (f.pan_number && !panRegex.test(f.pan_number.toUpperCase())) return 'PAN format: 5 letters + 4 digits + 1 letter (e.g., AAAAA1234A)';
    }
    if (step === 3) {
      if (!f.nda_signed) return 'You must agree to the NDA terms';
      if (!f.nda_signature) return 'Type your full name as digital signature';
    }
    return null;
  };

  const next = () => {
    const err = validate();
    if (err) { setError(err); return; }
    setError('');
    setStep(s => Math.min(s + 1, 4));
  };
  const prev = () => { setError(''); setStep(s => Math.max(s - 1, 0)); };
  const toggleTool = (t) => setF(p => ({ ...p, tools: p.tools.includes(t) ? p.tools.filter(x => x !== t) : [...p.tools, t] }));
  const toggleRole = (r) => setF(p => {
    const has = p.specialist_roles.includes(r);
    if (has) return { ...p, specialist_roles: p.specialist_roles.filter(x => x !== r) };
    if (p.specialist_roles.length >= 2) return p; // max 2
    return { ...p, specialist_roles: [...p.specialist_roles, r] };
  });

  const submit = async () => {
    if (f.password !== f.confirm_password) { setError('Passwords do not match'); return; }
    if (f.password.length < 6) { setError('Password min 6 characters'); return; }
    if (!f.nda_signed) { setError('Please sign the NDA'); return; }
    setSubmitting(true); setError('');
    try {
      const skills = typeof f.skills === 'string' ? f.skills.split(',').map(s => s.trim()).filter(Boolean) : f.skills;
      await axios.post(`${API}/auth/register`, {
        email: f.email, password: f.password, full_name: f.full_name, role: 'freelancer',
      });
      // Update profile with additional details
      const login = await axios.post(`${API}/auth/login`, { email: f.email, password: f.password });
      const token = login.data.token;
      await axios.put(`${API}/profile/update`, {
        phone: f.phone, skills, tools: f.tools, portfolio_url: f.portfolio_url,
        address: f.city, pan_number: f.pan_number, aadhar_number: f.aadhar_number,
        bank_account_number: f.bank_account_number, bank_ifsc: f.bank_ifsc,
        bio: `${f.years_experience} years experience`,
        specialist_roles: f.specialist_roles,
      }, { headers: { Authorization: `Bearer ${token}` } });
      // Sign NDA
      if (f.nda_signed) {
        await axios.put(`${API}/profile/update`, { nda_status: 'signed' }, { headers: { Authorization: `Bearer ${token}` } });
      }
      setSuccess(true);
    } catch (e) { setError(e.response?.data?.detail || 'Registration failed'); }
    setSubmitting(false);
  };

  if (success) return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
      <div className="text-center animate-fade-in">
        <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
        <h1 className="text-2xl font-heading font-bold text-white">Welcome to VawworldOS!</h1>
        <p className="text-sm text-zinc-400 mt-2">Your profile has been created. Login to access your dashboard.</p>
        <a href="/login" className="mt-6 inline-block h-9 px-6 bg-blue-600 text-white text-sm rounded-sm leading-9">Go to Login</a>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-4" data-testid="freelancer-register">
      <div className="w-full max-w-lg">
        <div className="text-center mb-5">
          <div className="inline-flex items-center gap-2 mb-1">
            <div className="w-8 h-8 bg-blue-600 rounded-sm flex items-center justify-center"><Monitor className="w-4 h-4 text-white" /></div>
            <span className="text-xl font-heading font-bold text-white">VawworldOS</span>
          </div>
          <p className="text-xs text-zinc-500 uppercase tracking-wider">Freelancer Registration</p>
        </div>

        {/* Progress */}
        <div className="flex items-center gap-1 mb-5">
          {STEPS.map((s, i) => (
            <div key={i} className="flex-1">
              <div className={`h-1 rounded-full ${i <= step ? 'bg-blue-500' : 'bg-zinc-800'}`} />
              <p className={`text-[8px] mt-1 text-center ${i === step ? 'text-blue-400' : 'text-zinc-700'}`}>{s}</p>
            </div>
          ))}
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-5">
          <h2 className="text-sm font-heading font-semibold text-white mb-4">{STEPS[step]}</h2>

          {/* STEP 0: Basic */}
          {step === 0 && (
            <div className="space-y-3">
              <div>
                <label className="text-[9px] text-zinc-500 uppercase">Full Name *</label>
                <input value={f.full_name} onChange={e => u('full_name', e.target.value)}
                  className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-sm text-white focus:ring-1 focus:ring-blue-500 focus:outline-none" placeholder="Your full name" />
              </div>
              <div>
                <label className="text-[9px] text-zinc-500 uppercase">Email *</label>
                <input type="email" value={f.email} onChange={e => u('email', e.target.value)}
                  className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-sm text-white focus:ring-1 focus:ring-blue-500 focus:outline-none" placeholder="your@email.com" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[9px] text-zinc-500 uppercase">Password *</label>
                  <input type="password" value={f.password} onChange={e => u('password', e.target.value)}
                    className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-sm text-white focus:outline-none" placeholder="Min 6 chars" />
                </div>
                <div>
                  <label className="text-[9px] text-zinc-500 uppercase">Confirm *</label>
                  <input type="password" value={f.confirm_password} onChange={e => u('confirm_password', e.target.value)}
                    className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-sm text-white focus:outline-none" placeholder="Repeat" />
                </div>
              </div>
              <div>
                <label className="text-[9px] text-zinc-500 uppercase">Phone *</label>
                <input value={f.phone} onChange={e => u('phone', e.target.value)}
                  className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-sm text-white focus:outline-none" placeholder="+91-9876543210" />
              </div>
              <div>
                <label className="text-[9px] text-zinc-500 uppercase">Your Expertise / Role * (select up to 2)</label>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {ROLE_OPTIONS.map(r => {
                    const selected = f.specialist_roles.includes(r.value);
                    const maxed = f.specialist_roles.length >= 2 && !selected;
                    return (
                      <button key={r.value} type="button" onClick={() => toggleRole(r.value)}
                        className={`px-3 py-1.5 rounded-sm text-[10px] border transition-all ${selected ? 'bg-blue-500/20 border-blue-500/50 text-blue-400 font-medium' : maxed ? 'bg-zinc-800/30 border-zinc-800 text-zinc-700 cursor-not-allowed' : 'bg-zinc-800/50 border-zinc-800 text-zinc-400 hover:border-zinc-600'}`}>
                        {r.label} {selected && <CheckCircle2 className="w-3 h-3 inline ml-0.5" />}
                      </button>
                    );
                  })}
                </div>
                {f.specialist_roles.length === 0 && <p className="text-[9px] text-amber-400 mt-1">You must select your role. This determines what tasks you get assigned.</p>}
              </div>
              <div>
                <label className="text-[9px] text-zinc-500 uppercase">City</label>
                <input value={f.city} onChange={e => u('city', e.target.value)}
                  className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-xs text-white focus:outline-none" placeholder="Mumbai, Delhi, Bengaluru..." />
              </div>
            </div>
          )}

          {/* STEP 1: Professional */}
          {step === 1 && (
            <div className="space-y-3">
              <div>
                <label className="text-[9px] text-zinc-500 uppercase">Skills (comma separated)</label>
                <input value={f.skills} onChange={e => u('skills', e.target.value)}
                  className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-xs text-white focus:outline-none" placeholder="3D Modeling, Texturing, Character Design" />
              </div>
              <div>
                <label className="text-[9px] text-zinc-500 uppercase mb-1.5 block">Tools I Use</label>
                <div className="flex flex-wrap gap-1.5">
                  {TOOLS.map(t => (
                    <button key={t} onClick={() => toggleTool(t)}
                      className={`px-2.5 py-1 rounded-sm text-[10px] border transition-all ${f.tools.includes(t) ? 'bg-blue-500/20 border-blue-500/50 text-blue-400' : 'bg-zinc-800/50 border-zinc-800 text-zinc-500 hover:border-zinc-600'}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[9px] text-zinc-500 uppercase">Years of Experience</label>
                <input type="number" value={f.years_experience} onChange={e => u('years_experience', e.target.value)}
                  className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-xs text-white focus:outline-none" placeholder="3" />
              </div>
              <div>
                <label className="text-[9px] text-zinc-500 uppercase">Portfolio URL</label>
                <input value={f.portfolio_url} onChange={e => u('portfolio_url', e.target.value)}
                  className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-xs text-white focus:outline-none" placeholder="https://artstation.com/yourname" />
              </div>
              <div>
                <label className="text-[9px] text-zinc-500 uppercase">Showreel URL (optional)</label>
                <input value={f.showreel_url} onChange={e => u('showreel_url', e.target.value)}
                  className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-xs text-white focus:outline-none" placeholder="https://vimeo.com/your-reel" />
              </div>
            </div>
          )}

          {/* STEP 2: KYC & Bank */}
          {step === 2 && (
            <div className="space-y-3">
              <p className="text-[10px] text-zinc-500">Required for payment processing. Your data is secure.</p>
              <div>
                <label className="text-[9px] text-zinc-500 uppercase">PAN Number *</label>
                <input value={f.pan_number} onChange={e => u('pan_number', e.target.value.toUpperCase())}
                  className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-xs text-white font-mono uppercase focus:outline-none" placeholder="AAAAA1234A" />
              </div>
              <div>
                <label className="text-[9px] text-zinc-500 uppercase">Aadhar Number</label>
                <input value={f.aadhar_number} onChange={e => u('aadhar_number', e.target.value)}
                  className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-xs text-white font-mono focus:outline-none" placeholder="1234-5678-9012" />
              </div>
              <div className="pt-2 border-t border-zinc-800">
                <p className="text-[9px] text-zinc-500 uppercase mb-2">Bank Details (for payments)</p>
                <div className="space-y-2">
                  <input value={f.bank_account_name} onChange={e => u('bank_account_name', e.target.value)}
                    className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-xs text-white focus:outline-none" placeholder="Account holder name" />
                  <input value={f.bank_account_number} onChange={e => u('bank_account_number', e.target.value)}
                    className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-xs text-white font-mono focus:outline-none" placeholder="Account number" />
                  <div className="grid grid-cols-2 gap-2">
                    <input value={f.bank_ifsc} onChange={e => u('bank_ifsc', e.target.value.toUpperCase())}
                      className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-xs text-white font-mono uppercase focus:outline-none" placeholder="IFSC code" />
                    <input value={f.bank_name} onChange={e => u('bank_name', e.target.value)}
                      className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-xs text-white focus:outline-none" placeholder="Bank name" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: NDA */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4 max-h-40 overflow-y-auto text-[10px] text-zinc-400 leading-relaxed">
                <p className="font-bold text-zinc-300 mb-2">NON-DISCLOSURE AGREEMENT</p>
                <p>This NDA is between VawWorld Media Pvt Ltd ("Company") and the Freelancer ("Artist").</p>
                <p className="mt-2">1. All project data, scripts, character designs, storyboards, animation files, and production materials are confidential.</p>
                <p className="mt-2">2. The Artist shall not disclose, copy, or distribute any confidential information.</p>
                <p className="mt-2">3. This NDA remains effective for 5 years from signing.</p>
                <p className="mt-2">4. Breach may result in legal action under Indian law.</p>
                <p className="mt-2">5. All materials must be returned upon project completion.</p>
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={f.nda_signed} onChange={e => u('nda_signed', e.target.checked)}
                  className="w-4 h-4 bg-zinc-800 border-zinc-700 rounded" />
                <span className="text-xs text-zinc-300">I agree to the NDA terms</span>
              </label>
              <div>
                <label className="text-[9px] text-zinc-500 uppercase">Digital Signature (type full name)</label>
                <input value={f.nda_signature} onChange={e => u('nda_signature', e.target.value)}
                  className="w-full h-10 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-lg text-white italic font-serif focus:outline-none" placeholder="Your full legal name" />
              </div>
            </div>
          )}

          {/* STEP 4: Review */}
          {step === 4 && (
            <div className="space-y-2">
              {[
                ['Name', f.full_name], ['Email', f.email], ['Phone', f.phone],
                ['Expertise', f.specialist_roles.map(r => ROLE_OPTIONS.find(o => o.value === r)?.label || r).join(', ')],
                ['City', f.city], ['PAN', f.pan_number], ['Aadhar', f.aadhar_number],
                ['Bank', `${f.bank_name} - ${f.bank_account_number}`],
                ['Skills', f.skills], ['Tools', f.tools.join(', ')],
                ['Experience', `${f.years_experience} years`],
                ['Portfolio', f.portfolio_url], ['NDA', f.nda_signed ? 'Signed' : 'NOT SIGNED'],
              ].map(([l, v], i) => (
                <div key={i} className="flex justify-between py-1 border-b border-zinc-800/30">
                  <span className="text-[10px] text-zinc-500">{l}</span>
                  <span className={`text-xs ${l === 'NDA' && !f.nda_signed ? 'text-red-400' : 'text-zinc-300'} font-mono text-right max-w-[60%] truncate`}>{v || '-'}</span>
                </div>
              ))}
            </div>
          )}

          {error && <p className="text-red-400 text-xs mt-3 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{error}</p>}

          <div className="flex justify-between mt-5 pt-3 border-t border-zinc-800">
            {step > 0 ? <button onClick={prev} className="h-9 px-4 bg-zinc-800 text-zinc-300 text-xs rounded-sm flex items-center gap-1"><ChevronLeft className="w-3.5 h-3.5" /> Back</button> : <div />}
            {step < 4 ? (
              <button onClick={next} className="h-9 px-5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium rounded-sm flex items-center gap-1">Next <ChevronRight className="w-3.5 h-3.5" /></button>
            ) : (
              <button onClick={submit} disabled={submitting || !f.nda_signed}
                className="h-9 px-6 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-medium rounded-sm disabled:opacity-40">
                {submitting ? 'Creating...' : 'Complete Registration'}
              </button>
            )}
          </div>
        </div>

        <p className="text-center text-[10px] text-zinc-700 mt-3">
          Already registered? <a href="/login" className="text-blue-400 hover:underline">Sign In</a>
          {' | '}<a href="/vendor/register" className="text-blue-400 hover:underline">Register as Vendor/Studio</a>
        </p>
      </div>
    </div>
  );
}
